import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];
const PROJECT_OPTS = [
    "Rénovation complète",
    "Construction maison individuelle",
    "Rénovation partielle",
    "Étude / conseil",
];

function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function nextBusinessDays(count = 14) {
    const arr = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    let d = new Date(start);
    d.setDate(d.getDate() + 1);
    while (arr.length < count) {
        const day = d.getDay(); // 0=Sun,6=Sat
        if (day !== 0) arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return arr;
}

const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS = [
    "Janv.", "Févr.", "Mars", "Avril", "Mai", "Juin",
    "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc.",
];

export default function Booking() {
    const days = useMemo(() => nextBusinessDays(12), []);
    const [selectedDate, setSelectedDate] = useState(days[0]);
    const [selectedSlot, setSelectedSlot] = useState("");
    const [bookedSlots, setBookedSlots] = useState([]);
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        project_type: PROJECT_OPTS[0],
        address: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const isoDate = toISO(selectedDate);

    useEffect(() => {
        axios
            .get(`${API}/appointment/booked`, { params: { date: isoDate } })
            .then((r) => setBookedSlots(r.data.booked || []))
            .catch(() => setBookedSlots([]));
        setSelectedSlot("");
    }, [isoDate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!selectedSlot) {
            toast.error("Sélectionnez un créneau horaire.");
            return;
        }
        if (!form.full_name || !form.email || !form.phone) {
            toast.error("Nom, email et téléphone sont requis.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/appointment`, {
                ...form,
                date: isoDate,
                time_slot: selectedSlot,
            });
            setDone(true);
            toast.success("Rendez-vous confirmé ! Vérifiez votre email.");
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error("Ce créneau vient d'être réservé. Choisissez-en un autre.");
                setBookedSlots((s) => [...s, selectedSlot]);
                setSelectedSlot("");
            } else {
                toast.error("Erreur lors de la réservation.");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            id="rendezvous"
            data-testid="booking"
            className="py-32 bg-[#F2EEE5] relative"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4 section-fade">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#B89C6A]" />
                            <span className="tracking-luxe text-[#B89C6A]">
                                Rendez-vous
                            </span>
                        </div>
                        <h2 className="font-serif-lux text-4xl md:text-5xl leading-[1.02]">
                            Nous venons <em className="italic text-[#B89C6A]">chez vous</em>.
                        </h2>
                        <p className="mt-6 text-[14px] text-[#1C1E1D]/70 leading-relaxed">
                            <b>Déplacement gratuit</b> dans toute l'Île-de-France et alentours. Notre équipe se rend directement sur le lieu de votre projet pour une étude sur place, sans engagement.
                        </p>
                        <div className="mt-10 space-y-4 text-[14px]">
                            <div className="flex items-start gap-4">
                                <span className="tracking-luxe text-[#B89C6A] w-24">Zone</span>
                                <span>Île-de-France &amp; alentours</span>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="tracking-luxe text-[#B89C6A] w-24">Frais</span>
                                <span className="font-serif-lux text-lg">Déplacement offert</span>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="tracking-luxe text-[#B89C6A] w-24">Durée</span>
                                <span>≈ 45 minutes sur site</span>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="tracking-luxe text-[#B89C6A] w-24">Téléphone</span>
                                <a
                                    href="tel:+33623762847"
                                    className="font-serif-lux text-lg hover:text-[#B89C6A] transition-colors"
                                >
                                    06 23 76 28 47
                                </a>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="tracking-luxe text-[#B89C6A] w-24">Email</span>
                                <span>contact@cmrenov.com</span>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-8 section-fade">
                        {done ? (
                            <div
                                data-testid="booking-success"
                                className="lux-card p-12 text-center"
                            >
                                <div className="tracking-luxe text-[#B89C6A]">
                                    Merci
                                </div>
                                <h3 className="font-serif-lux text-4xl mt-4">
                                    Rendez-vous confirmé
                                </h3>
                                <p className="mt-4 text-[15px] text-[#1C1E1D]/70">
                                    Nous vous attendons le{" "}
                                    <b>
                                        {selectedDate.toLocaleDateString("fr-FR", {
                                            weekday: "long",
                                            day: "numeric",
                                            month: "long",
                                        })}
                                    </b>{" "}
                                    à <b>{selectedSlot}</b>. Un email de confirmation vient de vous être envoyé.
                                </p>
                                <p className="mt-3 text-[13px] text-[#1C1E1D]/60">
                                    Notre équipe se déplace gratuitement à l'adresse indiquée.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="lux-card p-8 md:p-12">
                                {/* Date picker */}
                                <div className="tracking-luxe text-[#B89C6A] mb-4">
                                    Choisissez une date
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2 mb-8">
                                    {days.map((d) => {
                                        const active =
                                            toISO(d) === toISO(selectedDate);
                                        return (
                                            <button
                                                key={toISO(d)}
                                                type="button"
                                                data-testid={`date-${toISO(d)}`}
                                                onClick={() => setSelectedDate(d)}
                                                className={`flex-shrink-0 w-20 py-4 border text-center transition-all ${
                                                    active
                                                        ? "border-[#B89C6A] bg-[#B89C6A]/10"
                                                        : "border-[#1C1E1D]/15 hover:border-[#1C1E1D]/40"
                                                }`}
                                            >
                                                <div className="text-[11px] tracking-widest uppercase text-[#1C1E1D]/60">
                                                    {DAYS[d.getDay()]}
                                                </div>
                                                <div className="font-serif-lux text-2xl mt-1">
                                                    {d.getDate()}
                                                </div>
                                                <div className="text-[10px] text-[#1C1E1D]/50 mt-1">
                                                    {MONTHS[d.getMonth()]}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Slots */}
                                <div className="tracking-luxe text-[#B89C6A] mb-4">
                                    Créneaux disponibles
                                </div>
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-10">
                                    {SLOTS.map((s) => {
                                        const booked = bookedSlots.includes(s);
                                        const active = selectedSlot === s;
                                        return (
                                            <button
                                                key={s}
                                                type="button"
                                                data-testid={`slot-${s}`}
                                                disabled={booked}
                                                onClick={() => setSelectedSlot(s)}
                                                className={`py-3 border font-serif-lux text-lg transition-all ${
                                                    booked
                                                        ? "opacity-30 line-through cursor-not-allowed"
                                                        : active
                                                        ? "border-[#B89C6A] bg-[#B89C6A]/15"
                                                        : "border-[#1C1E1D]/20 hover:border-[#1C1E1D]/50"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Form fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    <input
                                        data-testid="booking-name"
                                        className="lux-input"
                                        placeholder="Nom et prénom"
                                        value={form.full_name}
                                        onChange={(e) =>
                                            setForm({ ...form, full_name: e.target.value })
                                        }
                                        required
                                    />
                                    <input
                                        data-testid="booking-email"
                                        type="email"
                                        className="lux-input"
                                        placeholder="Email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        required
                                    />
                                    <input
                                        data-testid="booking-phone"
                                        className="lux-input"
                                        placeholder="Téléphone"
                                        value={form.phone}
                                        onChange={(e) =>
                                            setForm({ ...form, phone: e.target.value })
                                        }
                                        required
                                    />
                                    <select
                                        data-testid="booking-project-type"
                                        className="lux-input bg-transparent"
                                        value={form.project_type}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                project_type: e.target.value,
                                            })
                                        }
                                    >
                                        {PROJECT_OPTS.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        data-testid="booking-address"
                                        className="lux-input md:col-span-2"
                                        placeholder="Adresse complète du projet — nous nous y déplaçons"
                                        value={form.address}
                                        onChange={(e) =>
                                            setForm({ ...form, address: e.target.value })
                                        }
                                        required
                                    />
                                    <textarea
                                        data-testid="booking-notes"
                                        className="lux-input md:col-span-2 min-h-[70px] resize-none"
                                        placeholder="Précisions sur votre projet (facultatif)"
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm({ ...form, notes: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="mt-10 flex justify-between items-center border-t hairline pt-8">
                                    <div className="text-[12px] text-[#1C1E1D]/60 max-w-xs">
                                        Sans engagement · réponse sous 24 h ouvrées.
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="booking-submit"
                                        disabled={busy}
                                        className="btn-champagne disabled:opacity-50"
                                    >
                                        {busy ? "Envoi…" : "Confirmer le rendez-vous"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
